import React, {useMemo} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {Card, Circle, FlexColumnSection, FlexRowSection, GreyText, SpanBold} from "../styles/globalStyles";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {regular} from "@fortawesome/fontawesome-svg-core/import.macro";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import useCountdownTimer from "../helpers/hooks/useCountdownTimer";
import useScreenSize from "../helpers/hooks/useScreenSize";



/**
 * Presentational countdown UI. Pair with useCountdownTimer in the parent
 * so the same timer state can drive CTAs / other UI.
 * variant 'card' - the full offer-step card; variant 'pill' - the compact header pill
 * (renders nothing when no timer is set, digits only on mobile).
 */
const CountdownTimer = (props) => {
    const {labelExpired, labelUrgent, timerLabel, onExpire, variant = 'card'} = props;
    const {remainingMs, timeDisplay, isUrgent, isExpired} = useCountdownTimer({onExpire});
    const {isMobile} = useScreenSize();

    const STATE_STYLES = useMemo(() => variant === 'pill' ? {
        normal: {bg: '#F9F8FF', border: '#DDD6FE', circle: '#EDE8FE', accent: '#6D28D9', label: '#7C3AED', alarm: false},
        urgent: {bg: Colors.urgentBg, border: Colors.urgentBorder, circle: Colors.urgentCircle, accent: Colors.urgentBorder, label: Colors.urgentBorder, alarm: true},
        expired: {bg: Colors.badgeRed, border: Colors.badgeRedText, circle: '#FFD4D4', accent: Colors.badgeRedText, label: Colors.badgeRedText, alarm: true},
    } : {
        normal: {bg: Colors.white, border: undefined, circle: Colors.siteBg, accent: Colors.siteBlack, alarm: false},
        urgent: {bg: Colors.urgentBg, border: Colors.urgentBorder, circle: Colors.urgentCircle, accent: Colors.urgentBorder, alarm: true},
        expired: {bg: Colors.badgeRed, border: Colors.badgeRedText, circle: '#FFD4D4', accent: Colors.badgeRedText, alarm: true},
    }, [variant]);
    const COLOR_TRANSITION = useMemo(() => 'background 600ms ease, border-color 600ms ease, color 600ms ease', []);
    const timeState = useMemo(() => STATE_STYLES[isExpired ? 'expired' : isUrgent ? 'urgent' : 'normal'], [isExpired, isUrgent]);

    const getLabel = () => {
        if(isExpired) return labelExpired ?? timerLabel
        if(isUrgent) return labelUrgent ?? timerLabel
        return timerLabel
    }

    if (variant === 'pill') {
        if (remainingMs == null) return null;
        return (
            <Pill style={{background: timeState.bg, borderColor: timeState.border, transition: COLOR_TRANSITION}}>
                <IconChip style={{background: timeState.circle, transition: COLOR_TRANSITION}}>
                    <FontAwesomeIcon icon={timeState.alarm ? regular('alarm-exclamation') : regular('clock')} color={timeState.accent} style={{fontSize: 13}}/>
                </IconChip>
                <Digits fontSize={'16px'} $pulse={isUrgent && !isExpired} style={{color: timeState.accent}}>{timeDisplay}</Digits>
                {!isMobile && (
                    <>
                        <PillDivider/>
                        <PillLabel style={{color: timeState.label}}>{getLabel()}</PillLabel>
                    </>
                )}
            </Pill>
        );
    }

    return (
        <Card flex={'unset'} overflow={'visible'} padding={'16px 20px'} style={{background: timeState.bg, borderColor: timeState.border, flexShrink: 0, transition: COLOR_TRANSITION}}>
            <FlexRowSection flex={'unset'} gap={'12px'} align={'center'}>
                <Circle style={{width: 40, height: 40, background: timeState.circle, flexShrink: 0, transition: COLOR_TRANSITION}}>
                    <FontAwesomeIcon icon={timeState.alarm ? regular('alarm-exclamation') : regular('clock')} color={timeState.accent} size={'lg'}/>
                </Circle>
                <FlexColumnSection flex={'unset'} overflow={'visible'} gap={'2px'}>
                    <Digits fontSize={'25px'} $pulse={isUrgent && !isExpired} style={{color: timeState.accent}}>{timeDisplay}</Digits>
                    <GreyText fontSize={'14px'} style={timeState.alarm ? {color: Colors.siteBlack, fontWeight: 600} : undefined}>{getLabel()}</GreyText>
                </FlexColumnSection>
            </FlexRowSection>
        </Card>
    );
};

export default CountdownTimer;

const pulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
`;

const Digits = styled(SpanBold)`
    transition: color 600ms ease;
    line-height: 1;
    ${({$pulse}) => $pulse && css`animation: ${pulse} 1s infinite;`}
`;

const Pill = styled(FlexRowSection)`
    flex: unset;
    gap: 10px;
    padding: 4px 12px;
    border-radius: 6px;
    border: 1px solid transparent;
    box-shadow: 0 1px 1.5px rgba(0, 0, 0, 0.08);
    flex-shrink: 0;
`;

const IconChip = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    padding: 0 5px;
    border-radius: 6px;
`;

const PillDivider = styled.div`
    width: 1px;
    height: 16px;
    background: #DDD6FE;
`;

const PillLabel = styled.span`
    font-size: 15px;
    white-space: nowrap;
    transition: color 600ms ease;
`;
